import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, MapPin, CreditCard, ShieldCheck, Upload, Check, Loader2, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = ["Identity", "Location", "Plan & Payment", "Confirm"];

export default function OnboardWizard() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [name, setName] = useState("");
  const [board, setBoard] = useState("CBSE");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Step 2
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);

  // Step 3
  const [plans, setPlans] = useState([]);
  const [planKey, setPlanKey] = useState("");
  const [pay, setPay] = useState(null);           // {gateway,currency,customer_id,setup_ref,client_secret,mock}
  const [payConfirmed, setPayConfirmed] = useState(null); // {payment_method_id,customer_id,gateway}
  const [payBusy, setPayBusy] = useState(false);

  // Step 4
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPw, setAdminPw] = useState("");

  const currency = country === "IN" ? "INR" : "USD";
  const gateway = country === "IN" ? "RAZORPAY" : "STRIPE";

  useEffect(() => { api.get("/geo/countries").then(r => setCountries(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/schools/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setState(""); setDistrict(""); setCity(""); setStates([]); setDistricts([]); setCities([]); setAvail(null);
    if (country) api.get("/geo/states", { params: { country_code: country } }).then(r => setStates(r.data));
  }, [country]);
  useEffect(() => {
    setDistrict(""); setCity(""); setDistricts([]); setCities([]);
    if (country && state) api.get("/geo/districts", { params: { country_code: country, state } }).then(r => setDistricts(r.data));
  }, [state]);
  useEffect(() => {
    setCity(""); setCities([]);
    if (country && state && district) api.get("/geo/cities", { params: { country_code: country, state, district } }).then(r => setCities(r.data));
  }, [district]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setPhoto(f); setPhotoPreview(URL.createObjectURL(f));
  };

  const checkAvailability = async () => {
    if (!country || !postal) { toast.error("Select country and enter postal code"); return; }
    setChecking(true); setAvail(null);
    try {
      const r = await api.get("/geo/check-availability", { params: { country_code: country, postal_code: postal } });
      setAvail(r.data);
    } catch { toast.error("Could not verify territory"); }
    finally { setChecking(false); }
  };

  const setupPayment = async () => {
    if (!planKey) { toast.error("Choose a plan"); return; }
    if (!adminEmail && !email) { toast.error("Enter your email first (Step 1)"); return; }
    setPayBusy(true); setPayConfirmed(null);
    try {
      const s = await api.post("/schools/payment/setup", {
        country_code: country, plan_key: planKey,
        admin_email: adminEmail || email, school_name: name,
      });
      setPay(s.data);
      const c = await api.post("/schools/payment/confirm", {
        gateway: s.data.gateway, setup_ref: s.data.setup_ref, customer_id: s.data.customer_id,
      });
      if (!c.data.payment_method_configured) { toast.error("Authorization failed"); return; }
      setPayConfirmed({ ...c.data, gateway: s.data.gateway });
      toast.success("Payment method authorized for trial");
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment setup failed"); }
    finally { setPayBusy(false); }
  };

  const canNext = useMemo(() => {
    if (step === 0) return name.trim() && phone.trim();
    if (step === 1) return country && state && district && city && postal && avail?.available;
    if (step === 2) return planKey && payConfirmed?.payment_method_configured;
    return true;
  }, [step, name, phone, country, state, district, city, postal, avail, planKey, payConfirmed]);

  const submit = async () => {
    if (!adminName.trim() || !adminEmail.trim() || adminPw.length < 6) {
      toast.error("Admin name, email, and a 6+ char password are required"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name, affiliation_board: board, phone, email,
        country_code: country, state, district, city, postal_code: postal,
        plan_key: planKey, payment_gateway: payConfirmed.gateway,
        payment_customer_id: payConfirmed.customer_id,
        payment_method_id: payConfirmed.payment_method_id,
        payment_method_configured: true,
        admin_name: adminName, admin_email: adminEmail, admin_password: adminPw,
      };
      const fd = new FormData();
      if (photo) fd.append("photo", photo);
      fd.append("payload", JSON.stringify(payload));
      const r = await api.post("/schools/onboard-territorial", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Onboarding failed"); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] grid place-items-center p-6">
        <div className="nb-card bg-[#86EFAC] p-8 max-w-lg w-full space-y-4 text-center" data-testid="onboard-success">
          <div className="w-16 h-16 rounded-full bg-white border-2 border-black grid place-items-center mx-auto">
            <Check size={32} />
          </div>
          <h1 className="display text-3xl font-black">Welcome to Ponderr!</h1>
          <p className="text-sm">Your school workspace is live. Your free trial runs until <b>{(done.trial_end || "").slice(0, 10)}</b>.</p>
          <div className="nb-card bg-white p-4 text-left text-sm mono space-y-1">
            <div>School: <b>{name}</b></div>
            <div>Admin login: <b>{done.admin_email}</b></div>
            <div>Plan: <b>{done.plan_key}</b></div>
            <div>Workspace slug: <b>{done.slug}</b></div>
          </div>
          <Link to="/login" className="nb-btn w-full justify-center" data-testid="onboard-goto-login">
            Go to Login <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0] p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="display text-3xl md:text-4xl font-black">Onboard Your School</h1>
          <Link to="/login" className="text-xs md:text-sm underline decoration-2 underline-offset-2" data-testid="onboard-demo-link">
            Want to explore first? Try our demo logins →
          </Link>
        </div>

        {/* Stepper */}
        <div className="flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 nb-card-sm p-2 text-center text-xs font-bold border-2 border-black ${i === step ? "bg-[#FDE047]" : i < step ? "bg-[#86EFAC]" : "bg-white"}`} data-testid={`onboard-step-${i}`}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        <div className="nb-card bg-white p-6 space-y-4">
          {step === 0 && (
            <div className="space-y-3" data-testid="onboard-panel-identity">
              <div className="flex items-center gap-2"><Building2 size={20} /><h2 className="display text-xl font-bold">School Identity</h2></div>
              <input className="nb-input" placeholder="School Name *" value={name} onChange={e => setName(e.target.value)} data-testid="onboard-name" />
              <div className="grid md:grid-cols-2 gap-3">
                <select className="nb-select" value={board} onChange={e => setBoard(e.target.value)} data-testid="onboard-board">
                  {["CBSE", "ICSE", "State Board", "IB", "Cambridge", "US Common Core", "Other"].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <input className="nb-input mono" placeholder="Phone *" value={phone} onChange={e => setPhone(e.target.value)} data-testid="onboard-phone" />
              </div>
              <input className="nb-input" placeholder="School Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="onboard-email" />
              <div className="border-2 border-dashed border-black rounded p-4 flex items-center gap-4">
                {photoPreview
                  ? <img src={photoPreview} alt="preview" className="w-24 h-20 object-cover border-2 border-black rounded" data-testid="onboard-photo-preview" />
                  : <div className="w-24 h-20 grid place-items-center bg-[#FAF7F0] border-2 border-black rounded text-[10px] text-center">SCHOOL PHOTO</div>}
                <label className="nb-btn nb-btn-secondary cursor-pointer">
                  <Upload size={14} /> Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="onboard-photo-input" />
                </label>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="onboard-panel-location">
              <div className="flex items-center gap-2"><MapPin size={20} /><h2 className="display text-xl font-bold">Location</h2></div>
              <div className="grid md:grid-cols-2 gap-3">
                <select className="nb-select" value={country} onChange={e => setCountry(e.target.value)} data-testid="onboard-country">
                  <option value="">Country…</option>
                  {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
                </select>
                <select className="nb-select" value={state} onChange={e => setState(e.target.value)} disabled={!country} data-testid="onboard-state">
                  <option value="">State…</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="nb-select" value={district} onChange={e => setDistrict(e.target.value)} disabled={!state} data-testid="onboard-district">
                  <option value="">District…</option>
                  {districts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="nb-select" value={city} onChange={e => setCity(e.target.value)} disabled={!district} data-testid="onboard-city">
                  <option value="">City…</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <input className="nb-input mono flex-1" placeholder="Postal / ZIP / Pincode *" value={postal} onChange={e => { setPostal(e.target.value); setAvail(null); }} data-testid="onboard-postal" />
                <button className="nb-btn" onClick={checkAvailability} disabled={checking} data-testid="onboard-check-btn">
                  {checking ? <Loader2 size={14} className="animate-spin" /> : "Check Territory"}
                </button>
              </div>
              {avail && (
                <div className={`nb-card-sm p-3 border-2 border-black font-bold text-sm ${avail.available ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`} data-testid="onboard-avail-badge">
                  {avail.available ? "🟢 Territory Available" : "🔴 Territory Claimed"} — {avail.message}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="onboard-panel-plan">
              <div className="flex items-center gap-2"><CreditCard size={20} /><h2 className="display text-xl font-bold">Plan & Payment</h2>
                <span className="nb-badge bg-[#93C5FD] ml-auto text-xs">{currency} · {gateway}</span>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {plans.map(p => (
                  <button key={p.plan_key} onClick={() => { setPlanKey(p.plan_key); setPayConfirmed(null); }}
                    className={`nb-card p-4 text-left border-2 border-black ${planKey === p.plan_key ? "bg-[#FDE047]" : "bg-white"}`}
                    data-testid={`onboard-plan-${p.plan_key}`}>
                    <div className="display text-lg font-bold">{p.name}</div>
                    <div className="display text-2xl font-black mono">
                      {currency === "INR" ? `₹${p.base_annual_inr.toLocaleString()}` : `$${p.base_annual_usd.toLocaleString()}`}
                      <span className="text-xs font-normal">/yr</span>
                    </div>
                    <div className="text-xs mt-1">Up to {p.max_students} students</div>
                    <div className="text-xs">{p.trial_days}-day free trial</div>
                  </button>
                ))}
              </div>
              {planKey && (
                <div className="nb-card-sm p-3 bg-[#FAF7F0] border-2 border-black space-y-2">
                  <div className="text-sm">Authorize a payment method to start your free trial. You won't be charged until the trial ends.
                    {gateway === "RAZORPAY" && <span className="mono text-xs block mt-1 opacity-70">Razorpay (test/mock) — INR</span>}
                    {gateway === "STRIPE" && <span className="mono text-xs block mt-1 opacity-70">Stripe test mode — card 4242…</span>}
                  </div>
                  {payConfirmed?.payment_method_configured
                    ? <div className="nb-badge bg-[#86EFAC] text-xs" data-testid="onboard-pay-ok"><Check size={12} /> Payment method authorized</div>
                    : <button className="nb-btn" onClick={setupPayment} disabled={payBusy} data-testid="onboard-pay-btn">
                        {payBusy ? <Loader2 size={14} className="animate-spin" /> : "Authorize Card (test)"}
                      </button>}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="onboard-panel-confirm">
              <div className="flex items-center gap-2"><ShieldCheck size={20} /><h2 className="display text-xl font-bold">Admin & Confirm</h2></div>
              <input className="nb-input" placeholder="Admin Full Name *" value={adminName} onChange={e => setAdminName(e.target.value)} data-testid="onboard-admin-name" />
              <input className="nb-input" placeholder="Admin Email *" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} data-testid="onboard-admin-email" />
              <input type="password" className="nb-input" placeholder="Admin Password (min 6) *" value={adminPw} onChange={e => setAdminPw(e.target.value)} data-testid="onboard-admin-pw" />
              <div className="nb-card-sm p-3 bg-[#FAF7F0] border-2 border-black text-sm space-y-1">
                <div><b>{name}</b> · {board}</div>
                <div>{city}, {state} · {postal}</div>
                <div>Plan: <b>{planKey}</b> ({currency})</div>
                <div>Payment: <b>{payConfirmed?.gateway}</b> ✓ authorized</div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button className="nb-btn nb-btn-ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="onboard-back">
              <ArrowLeft size={14} /> Back
            </button>
            {step < 3
              ? <button className="nb-btn" disabled={!canNext} onClick={() => setStep(s => s + 1)} data-testid="onboard-next">
                  Next <ArrowRight size={14} />
                </button>
              : <button className="nb-btn" disabled={submitting} onClick={submit} data-testid="onboard-submit">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : "Create School Workspace"}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
}
